import React, { Component, createRef } from "react"
import { RouteComponentProps } from "react-router"
import Radium from "radium"
import { connect } from "react-redux"
import { GridSize } from "@material-ui/core/Grid"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import { mdiFileDocumentMultiple, mdiMessageAlert, mdiPlaylistPlus, mdiRefresh } from "@mdi/js"
import { Table } from "simplygrow-ui"

import NavBar from "../../components/Navigation/NavBar"
import SectionHeader, { SectionHeaderComponent } from "../../components/Headings/SectionHeaderPrimary"
import LoadingModal from "../../components/Modals/LoadingModal"
import AlertModalOneButton from "../../components/Modals/AlertModalOneButton"
import SideMenu from "../../components/Navigation/SideMenu"
import { DocValiditySideMenu, ViewModes } from "../../components/SideMenus/DocValiditySideMenu"
import DownloadQueueModal from "../../components/Modals/DownloadQueueModal"
import { removeUnderScores, sentenceCase, toUpperCaseCustom } from "../../utils"
import { ColorPalette } from "../../config/colors"
import { ActionType } from "../../store/actions/actions"
import { DataCaptureMode, DocumentCategories, ProcessModes } from "../../config"
import { DocValidityRecord } from "../../models/docValidity/docValidityRecord"
import { DocServiceFactory, DocsService, DocValidityService, SessionService, TableService } from "../../services"
import { PeopleFlowCombinedReducer } from "../../store"
import { Toolbar, ActionButtonType } from "../../components/GeneralUI/Toolbar"
import { AssociationSettingsRepository, ProfileRepository, UsersRepository } from "../../repositories"
import {
  AsyncTaskDocExport,
  TimeBasedDocStatus,
  ValidEmploymentStatus,
  UiCategoryEnum,
  NavMenuAccess,
  ColumnConfig,
  AssociationId,
  DocName,
  DocsByProfilePk,
} from "../../types"
import { ProfileService } from "../../services"
import { DocValidityStatusEnum } from "../../enums"
import { TimeBasedDocStatuses } from "../../constants"
import { getRemoteFile } from "../../providers/remoteDataProvider"
import { TABLE_ROW_HEIGHT } from "../../constants/table"
import { DocDownloadQueueItem } from "../DocExports/DocExport"

dayjs.extend(relativeTime)
const profileService = new ProfileService()
const sessionService = new SessionService()
const docValidityService = new DocValidityService()
const tableService = new TableService()
const docsService = DocServiceFactory.create()

const columnConfig = [
  { key: "idPassport", label: "ID/Passport", sizeFactor: 0.9 },
  { key: "competency", label: "Document", sizeFactor: 1.15 },
  { key: "status", label: "Status", sizeFactor: 0.75 },
  { key: "expiryDate", label: "Expiry Date", sizeFactor: 0.95 },
  { key: "workSite", label: "Work Site", sizeFactor: 1.15 },
  { key: "employmentStatus", label: "Employment", sizeFactor: 1 },
]

const perDocConfig = [
  { key: "docType", label: "Document", sizeFactor: 1.5 },
  { key: DocValidityStatusEnum.NO_DOC, label: "No Doc", sizeFactor: 1 },
  { key: DocValidityStatusEnum.DOCS_OUTSTANDING, label: "Documents outstanding", sizeFactor: 1 },
  { key: DocValidityStatusEnum.EXPIRED, label: "Expired", sizeFactor: 1 },
  { key: DocValidityStatusEnum.VALID, label: "Valid", sizeFactor: 1 },
  { key: DocValidityStatusEnum.EXPIRES_IN_45_DAYS, label: "Expires (45 days)", sizeFactor: 1 },
]

interface DocValidityProps extends RouteComponentProps {
  comingFromDocValidity: boolean
  asyncTaskQueueDocExport: AsyncTaskDocExport[]
  associationRepo: AssociationSettingsRepository
  profileRepo: ProfileRepository
  userRepo: UsersRepository
  selectedAssociation: string
  selectedCohort: string
  selectedRole: string
  currentScreen: UiCategoryEnum
  currentSection: UiCategoryEnum
  previousScreen: UiCategoryEnum
  navMenuAccess: NavMenuAccess
  selectedEmploymentStatus: ValidEmploymentStatus
  profilePic: string
  idPassport: string
  password: string
  associationsWithInitialProfileLoad: Record<string, boolean>
  docValidityFilterState: any | undefined
  docValidityViewMode: ViewModes | undefined
  itemsBeingGenerated: DocDownloadQueueItem[]
  updateState: (data: any) => void
  resetDocValidityCachedState: () => void
}

interface DocValidityState {
  downloadQueueModalOpen: boolean
  loadingModalOpen: boolean
  warningModalOpen: boolean
  loadingModalMessage: string | React.ReactElement
  loadingModalAuxMessage: string
  warningModalHeader: string
  warningModalMessage: string
  isUpdatingDocExportQueue: boolean
  tableDataPerDocTable: Record<string, any>[]
  tableDataPerPersonTable: Record<string, any>[]
  columnConfigPerPersonTable: ColumnConfig[]
  tableWidth: GridSize
  searchString: string
  tableDataAllTable: Record<string, string>[]
  viewMode: ViewModes
  sideMenuVisible: boolean
  chartConfig: { title: string; value: number; color: string }[]
  cacheTimestamp: number
  checkTimestamp: number
  uiRefreshTimestamp: number
  profilesFetched: number
  fetchingProfiles: boolean
  profileRefetchInProgress: boolean
  tableHeight: number
  selectedRowIds: string[]
  initialFilterState: any | undefined
  currentFilterState: any | undefined
  itemsDownloading: string[]
}

const sectionHeaderRef: React.RefObject<SectionHeaderComponent> = createRef()
const tableRef: React.RefObject<HTMLDivElement> = createRef()

class DocValidity extends Component<DocValidityProps, DocValidityState> {
  labelMap: Record<string, string> = {
    [DocValidityStatusEnum.NO_DOC]: "No Doc",
    [DocValidityStatusEnum.DOCS_OUTSTANDING]: "Documents outstanding",
    [DocValidityStatusEnum.EXPIRED]: "Expired",
    [DocValidityStatusEnum.VALID]: "Valid",
    [DocValidityStatusEnum.EXPIRES_IN_45_DAYS]: "Expires in 45 days",
  }

  DocService = DocServiceFactory.create()

  initialModalState = {
    loadingModalOpen: false,
    warningModalOpen: false,
    downloadQueueModalOpen: false,
  }

  state: DocValidityState = {
    ...this.initialModalState,
    loadingModalMessage: "",
    warningModalHeader: "",
    warningModalMessage: "",
    loadingModalAuxMessage: "",
    searchString: "",
    chartConfig: [],
    tableDataAllTable: [],
    tableDataPerDocTable: [],
    tableDataPerPersonTable: [],
    columnConfigPerPersonTable: [],
    tableWidth: 9,
    isUpdatingDocExportQueue: false,
    sideMenuVisible: true,
    viewMode: ViewModes.PER_DOCUMENT,
    cacheTimestamp: 0,
    checkTimestamp: Date.now(),
    uiRefreshTimestamp: 0,
    profilesFetched: 0,
    fetchingProfiles: false,
    profileRefetchInProgress: false,
    tableHeight: 500,
    selectedRowIds: [],
    initialFilterState: undefined,
    currentFilterState: undefined,
    itemsDownloading: [],
  }

  componentDidMount() {
    this.reload()
  }

  componentDidUpdate(): void {
    if (tableRef.current && this.state.tableHeight === 500) {
      const tableTopY = tableRef.current.getBoundingClientRect().top
      const browserWindowHeight = window.innerHeight
      this.setState({ tableHeight: browserWindowHeight - tableTopY })
    }
  }

  reload = async () => {
    this.setState({
      loadingModalOpen: true,
      loadingModalMessage: "Loading...",
    })
    const { selectedAssociation, associationRepo, profileRepo, docValidityViewMode, docValidityFilterState } =
      this.props
    setTimeout(async () => {
      const maybeCache = await associationRepo.getDocValidityCache(selectedAssociation)
      this.initialiseRenderData(
        selectedAssociation,
        profileRepo,
        associationRepo,
        maybeCache,
        docValidityViewMode,
        docValidityFilterState,
      )
    }, 1000)
  }

  refreshAllData = async () => {
    const { selectedAssociation, associationRepo, profileRepo } = this.props
    await this.refreshAllProfiles()
    this.initialiseRenderData(selectedAssociation, profileRepo, associationRepo, undefined, undefined, undefined)
  }

  initialiseRenderData = async (
    selectedAssociation: AssociationId,
    profileRepo: ProfileRepository,
    associationRepo: AssociationSettingsRepository,
    docValidityRecordCache: DocValidityRecord[] | undefined,
    docValidityViewMode: ViewModes | undefined,
    docValidityFilterState: any | undefined,
  ) => {
    try {
      const loadingMessage = "Loading..."
      this.setState({
        loadingModalOpen: true,
        loadingModalMessage: loadingMessage,
        loadingModalAuxMessage: "",
      })

      let recordEntities = docValidityRecordCache
      if (!recordEntities) {
        console.log("STARTING")

        recordEntities = docValidityService.buildPreRequisiteReport(selectedAssociation, profileRepo, associationRepo)
        await associationRepo.putDocValidityCache(selectedAssociation, recordEntities)
      }
      const {
        chartConfig,
        tableDataAllTable,
        tableDataPerDocTable,
        tableDataPerPersonTable,
        columnConfigPerPersonTable,
      } = this.buildRenderData(recordEntities)

      this.startTimeStampUpdateInterval()
      this.setState({
        ...this.initialModalState,
        chartConfig,
        tableDataAllTable,
        tableDataPerDocTable,
        tableDataPerPersonTable,
        columnConfigPerPersonTable,
        uiRefreshTimestamp: Date.now(),
        initialFilterState: docValidityFilterState,
        viewMode: docValidityViewMode || ViewModes.PER_DOCUMENT,
      })
      this.props.updateState({ docValidityFilterState: undefined, docValidityViewMode: undefined })
    } catch (error: any) {
      this.displayWarning(error)
    }
  }

  async refreshAllProfiles() {
    const { associationRepo, profileRepo, idPassport, password, selectedAssociation, selectedCohort } = this.props
    const loadingModalMessage = "Refreshing profiles..."
    try {
      this.setState({
        ...this.initialModalState,
        loadingModalOpen: true,
        loadingModalMessage,
        loadingModalAuxMessage: "This may take a few minutes",
      })

      const IS_FRESH_DOWNLOAD = false
      await profileService.downloadAssociationProfiles(
        IS_FRESH_DOWNLOAD,
        sessionService,
        associationRepo,
        profileRepo,
        { username: idPassport, password },
        selectedAssociation,
        selectedCohort,
        (progress: string) => {
          this.setState({
            ...this.initialModalState,
            loadingModalOpen: true,
            loadingModalMessage,
            loadingModalAuxMessage: progress,
          })
        },
      )
      const cacheTimestamp = await profileRepo.getProfilesDownloadedMs(selectedCohort)
      this.setState({ ...this.initialModalState, cacheTimestamp })
    } catch (error: any) {
      this.displayWarning(error)
    }
  }

  closeModals() {
    this.setState({ ...this.initialModalState })
  }

  startTimeStampUpdateInterval = () => {
    this.setState({ checkTimestamp: Date.now() })
    setTimeout(this.startTimeStampUpdateInterval, 60000)
  }

  buildRenderData = (docValidityRecords: DocValidityRecord[]) => {
    const tableDataAllTable = this.generateTableDataAll(docValidityRecords)
    const { tableDataPerDocTable, chartConfig } = this.generateTableDataPerDoc(docValidityRecords)
    const { tableDataPerPersonTable, columnConfigPerPersonTable } = this.generateTablePerPerson(docValidityRecords)
    return { chartConfig, tableDataAllTable, tableDataPerDocTable, tableDataPerPersonTable, columnConfigPerPersonTable }
  }

  changeViewMode = (selectedViewMode: ViewModes) => {
    this.setState({ viewMode: selectedViewMode, uiRefreshTimestamp: new Date().getTime() })
  }

  generateRingChartConfig(
    allDocStatuses: TimeBasedDocStatus[],
    perDocData: Record<string, any>[],
  ): { title: string; value: number; color: string }[] {
    const chartConfig = allDocStatuses.map((status) => {
      const colourMap: Record<string, string> = {
        [DocValidityStatusEnum.VALID]: ColorPalette.PRIMARY_BLUE,
        [DocValidityStatusEnum.EXPIRES_IN_45_DAYS]: ColorPalette.DARK_GREY,
        [DocValidityStatusEnum.EXPIRED]: ColorPalette.WARNING_RED,
        [DocValidityStatusEnum.NO_DOC]: ColorPalette.VERY_LIGHT_GREY,
        [DocValidityStatusEnum.DOCS_OUTSTANDING]: ColorPalette.VERY_LIGHT_GREY,
      }
      let configItem = {
        title: status in this.labelMap ? this.labelMap[status] : status,
        value: 0,
        color: colourMap[status],
      }
      perDocData.forEach((summaryItem) => (configItem.value += parseInt(summaryItem[status])))
      return configItem
    })
    return chartConfig
  }

  generateTableDataAll(allDocData: DocValidityRecord[]): Record<string, string>[] {
    const tableDataAllTable = allDocData.map((docInstance: DocValidityRecord) => {
      let metaData = docInstance.getMetaData()
      metaData.status = removeUnderScores(metaData.status)
      if (toUpperCaseCustom(metaData.status) === toUpperCaseCustom(DocValidityStatusEnum.VALID_CANT_EXPIRE)) {
        metaData.status = DocValidityStatusEnum.VALID
      }
      return {
        ...metaData,
        id: `${metaData.idPassport}___${metaData.competency}___${metaData.expiryDate}`,
      }
    })
    return tableDataAllTable
  }

  generateTableDataPerDoc(allValidityRecords: DocValidityRecord[]): {
    tableDataPerDocTable: Record<string, any>[]
    chartConfig: { title: string; value: number; color: string }[]
  } {
    let perDocData = [] as Record<string, any>[]
    const allDocStatuses = TimeBasedDocStatuses.filter((status) => status !== DocValidityStatusEnum.VALID_CANT_EXPIRE)
    const statsPerDoc = docValidityService.getCategorisedPreRequisiteStats(allValidityRecords)
    Object.keys(statsPerDoc).forEach((docType) => {
      let tableRecord = { docType, id: docType } as Record<string, any>
      allDocStatuses.forEach((status) => {
        tableRecord[status] = status in statsPerDoc[docType] ? statsPerDoc[docType][status].toString() : "0"
      })
      perDocData.push(tableRecord)
    })
    const chartConfig = this.generateRingChartConfig(allDocStatuses, perDocData)
    return { tableDataPerDocTable: perDocData, chartConfig }
  }

  generateTablePerPerson(allValidityRecords: DocValidityRecord[]) {
    const { profileRepo, selectedAssociation, associationRepo } = this.props
    const { NO_DOC } = DocValidityStatusEnum
    const filteredPreRequisiteNames = docValidityService
      .getAllPreRequisiteNames(allValidityRecords)
      .filter((preRequisiteName: string) => !(preRequisiteName === NO_DOC || preRequisiteName === "NONE"))
    const { infoFieldColumns, preRequisiteColumns } = this.buildColumnConfigPerPerson(
      associationRepo,
      selectedAssociation,
      filteredPreRequisiteNames,
    )
    const columnConfigPerPersonTable = [...infoFieldColumns, ...preRequisiteColumns]
    const tableDataPerPersonTable = this.buildTableDataPerPerson(
      profileRepo,
      infoFieldColumns,
      allValidityRecords,
      filteredPreRequisiteNames,
    )

    return { tableDataPerPersonTable, columnConfigPerPersonTable }
  }

  buildColumnConfigPerPerson(
    associationRepo: AssociationSettingsRepository,
    associationId: AssociationId,
    preRequisiteNames: DocName[],
  ): { infoFieldColumns: ColumnConfig[]; preRequisiteColumns: ColumnConfig[] } {
    const getStatusDotColor = (validityStatus: string) => {
      const statusDotColorMap = {
        [DocValidityStatusEnum.NO_EXPIRY_SET]: ColorPalette.MEDIUM_GREY,
        [DocValidityStatusEnum.VALID]: ColorPalette.PRIMARY_BLUE,
        [DocValidityStatusEnum.EXPIRES_IN_45_DAYS]: ColorPalette.DARK_GREY,
        [DocValidityStatusEnum.EXPIRED]: ColorPalette.WARNING_RED,
      } as Record<string, string>
      if (validityStatus in statusDotColorMap) {
        return statusDotColorMap[validityStatus]
      }
      return ColorPalette.VERY_LIGHT_GREY
    }

    const renderStatusDot = (params: any) => {
      let dot = <div style={{ ...styles.statusDot, backgroundColor: ColorPalette.VERY_LIGHT_GREY }} />
      if (params.value) {
        dot = <div style={{ ...styles.statusDot, backgroundColor: getStatusDotColor(params.value) }} />
      }
      return <div style={styles.statusDotContainer}>{dot}</div>
    }

    const preRequisiteColumns = preRequisiteNames.map((preRequisiteName: string) => ({
      key: preRequisiteName,
      label: sentenceCase(removeUnderScores(preRequisiteName)),
      sizeFactor: 1,
      renderCell: renderStatusDot,
    })) as ColumnConfig[]

    function buildInfoFieldColumns(): ColumnConfig[] {
      let infoFieldColumns = [{ key: "idPassport", label: "ID/Passport", sizeFactor: 2 }]
      const organisationConfig = associationRepo.getOrganisationConfig(associationId)
      const fieldConfig = associationRepo.getFieldConfig(associationId)
      if (!organisationConfig.docValidityColumnConfigPerPerson) {
        return infoFieldColumns
      }
      const configuredColumns = organisationConfig.docValidityColumnConfigPerPerson.map((column: ColumnConfig) => {
        let label = column.label
        if (!label) {
          const field = fieldConfig[column.key]
          label = field ? field.label : toUpperCaseCustom(column.key)
        }
        return { key: column.key, label, sizeFactor: column.sizeFactor ?? 2 }
      })
      return [...infoFieldColumns, ...configuredColumns]
    }
    const infoFieldColumns = buildInfoFieldColumns()
    return { infoFieldColumns, preRequisiteColumns }
  }

  buildTableDataPerPerson(
    profileRepo: ProfileRepository,
    infoFieldColumns: ColumnConfig[],
    allValidityRecords: DocValidityRecord[],
    preRequisiteNames: DocName[],
  ) {
    const addInfoColumnData = (
      idPassport: string,
      tableRecord: Record<string, any>,
      infoFieldColumns: ColumnConfig[],
    ) => {
      infoFieldColumns.forEach((column) => {
        const field = column.key
        if (field === "idPassport") {
          return
        }
        const profileEntity = profileRepo.getProfileEntityByIdPassport(idPassport)
        if (!profileEntity) {
          return
        }
        tableRecord[field] = profileEntity.getGeneralDataValue(field, "-")
      })
    }

    let tableDataPerPersonTable = [] as Record<string, any>[]
    const statsPerPerson = docValidityService.getCategorisedDocsByPerson(allValidityRecords)
    Object.keys(statsPerPerson).forEach((idPassport) => {
      let tableRecord = { idPassport, id: idPassport } as Record<string, any>
      addInfoColumnData(idPassport, tableRecord, infoFieldColumns)

      statsPerPerson[idPassport].forEach((docInstance: DocValidityRecord) => {
        const { name, surname } = docInstance.getMetaData()
        const docType = docInstance.getPreRequisiteName()
        let docStatus = docInstance.getStatus()
        const expiryDate = docInstance.getExpiryDate()
        if (expiryDate === "1970-01-01") {
          docStatus = DocValidityStatusEnum.NO_EXPIRY_SET
        }
        tableRecord = { ...tableRecord, [docType]: docStatus, name, surname }
      })
      preRequisiteNames.forEach((preRequisiteName: string) =>
        tableRecord[preRequisiteName] ? null : (tableRecord[preRequisiteName] = ""),
      )
      tableDataPerPersonTable.push(tableRecord)
    })
    return tableDataPerPersonTable
  }

  generateDocs = async () => {
    try {
      function generateDocBatches(allTargetDocs: DocsByProfilePk) {
        const BATCH_SIZE = 1000
        let counter = 0
        let batchCounter = 0
        const docBatches = [] as DocsByProfilePk[]
        docBatches[batchCounter] = {}
        Object.keys(allTargetDocs).forEach((profilePk) => {
          docBatches[batchCounter][profilePk] = allTargetDocs[profilePk]
          counter += allTargetDocs[profilePk].length
          if (counter > BATCH_SIZE) {
            batchCounter++
            docBatches[batchCounter] = {}
            counter = 0
          }
        })
        return docBatches
      }
      async function createAsyncTasks(
        docBatches: DocsByProfilePk[],
        docsService: DocsService,
        userRepo: UsersRepository,
        token: string,
      ) {
        docBatches = [...docBatches].filter((docBatch) => Object.keys(docBatch).length)
        let asyncTasks = [] as AsyncTaskDocExport[]
        const user = userRepo.getCurrentUserEntity()
        const { name, surname } = user.getPersonalUserInfo()
        for (let i = 0; i < docBatches.length; i++) {
          const docBatch = docBatches[i]
          const asyncTask = await docsService.triggerDocBatchGeneration(
            docBatch,
            selectedAssociation,
            docsService.getDownloadBatchLabel(docBatches.length - i, name, surname),
            token,
          )
          asyncTasks.unshift(asyncTask)
        }
        return asyncTasks
      }

      this.setState({
        ...this.initialModalState,
        loadingModalOpen: true,
        loadingModalMessage: "Triggering document generation...",
      })
      const { tableDataAllTable, selectedRowIds } = this.state
      const { idPassport, password, selectedAssociation, userRepo, itemsBeingGenerated } = this.props

      let targetDocs = {} as DocsByProfilePk
      tableDataAllTable.forEach((item) => {
        if (selectedRowIds.includes(item.id)) {
          if (!targetDocs[item.idPassport]) {
            targetDocs[item.idPassport] = []
          }
          targetDocs[item.idPassport].push(item.filename)
        }
      })
      const docBatches = generateDocBatches(targetDocs)
      const token = await SessionService.prepareAuthTokens(idPassport, password)
      const asyncTasks = await createAsyncTasks(docBatches, docsService, userRepo, token)
      if (!asyncTasks.length) {
        throw { code: "NoDocs" }
      }
      this.props.updateState({
        itemsBeingGenerated: [...itemsBeingGenerated, ...DocsService.asyncTasksToDownloadQueueItems(asyncTasks)],
      })
      this.updateQueueData(true)
    } catch (error) {
      console.error("error: ", error)
      this.displayWarning(error)
    }
  }

  async downloadFile(filePath: string, queueId: string): Promise<any> {
    try {
      const { idPassport, password, selectedAssociation } = this.props
      const { itemsDownloading = [] } = this.state
      if (itemsDownloading.length && !itemsDownloading.includes(queueId)) {
        throw { code: "DownloadInProgress" }
      }
      let newItemsDownloading = [...new Set([...itemsDownloading, queueId])]
      this.setState({ itemsDownloading: newItemsDownloading })
      await docsService.downloadDocBatch(filePath)
      newItemsDownloading = newItemsDownloading.filter((existingQueueId) => existingQueueId !== queueId)
      this.setState({ itemsDownloading: newItemsDownloading })
      const token = await SessionService.prepareAuthTokens(idPassport, password)
      const asyncTaskQueueDocExport = await docsService.clearDownloadQueue(selectedAssociation, queueId, token)
      this.props.updateState({ asyncTaskQueueDocExport })
      this.forceUpdate()
    } catch (error) {
      this.displayWarning(error)
    }
  }

  async updateQueueData(showModal?: boolean) {
    function filterItemsBeingGenerated(
      asyncTaskQueueDocExport: AsyncTaskDocExport[] = [],
      itemsBeingGenerated: DocDownloadQueueItem[] = [],
    ) {
      const queueIds = [...asyncTaskQueueDocExport].map((item) => item.sk)
      itemsBeingGenerated = itemsBeingGenerated.filter(({ queueId }) => !queueIds.includes(queueId))
      return itemsBeingGenerated
    }
    if (showModal) {
      this.setState({ ...this.initialModalState, downloadQueueModalOpen: true, isUpdatingDocExportQueue: true })
    }
    let { idPassport, password, selectedAssociation, itemsBeingGenerated } = this.props
    const token = await SessionService.prepareAuthTokens(idPassport, password)
    const asyncTaskQueueDocExport = await docsService.fetchDocDownloadQueue(selectedAssociation, token)
    itemsBeingGenerated = filterItemsBeingGenerated(asyncTaskQueueDocExport, itemsBeingGenerated)

    this.props.updateState({ asyncTaskQueueDocExport, itemsBeingGenerated })
    this.setState({ isUpdatingDocExportQueue: false })
  }

  rowSelectionHandler = (selectedRowIds: string[]) => {
    this.setState({ selectedRowIds })
  }

  searchHandler(event: React.ChangeEvent<{ value: string }>) {
    this.setState({ searchString: event.target.value })
  }

  setFilter = (rowItemId: string, field: string) => {
    const updatedFilterState = { items: [{ field, operator: "contains", value: rowItemId }] }
    this.setState({
      viewMode: ViewModes.ALL,
      initialFilterState: updatedFilterState,
      currentFilterState: updatedFilterState,
      uiRefreshTimestamp: new Date().getTime(),
    })
  }

  packageCacheState() {
    const {
      chartConfig,
      tableDataAllTable,
      tableDataPerDocTable,
      tableDataPerPersonTable,
      columnConfigPerPersonTable,
      currentFilterState,
      viewMode,
    } = this.state
    return {
      isValidCache: true,
      filterState: currentFilterState,
      viewMode,
      chartConfig,
      tableDataAllTable,
      tableDataPerDocTable,
      tableDataPerPersonTable,
      columnConfigPerPersonTable,
    }
  }

  async selectProfile(rowItemId: string) {
    try {
      this.setState(
        {
          ...this.initialModalState,
          loadingModalOpen: true,
          loadingModalMessage: "Preparing profile info...",
        },
        async () => {
          const { selectedEmploymentStatus, profileRepo } = this.props
          const { viewMode, currentFilterState } = this.state
          const profileIdPassport = rowItemId.split("___")[0]
          const profile = profileRepo.getProfileEntityByIdPassport(profileIdPassport)
          if (profile === undefined) {
            this.setState({
              ...this.initialModalState,
              warningModalOpen: true,
              warningModalHeader: "Profile Not Found",
              warningModalMessage: "This profile hasn't been downloaded. Click REFRESH and try again.",
            })
            return
          }

          const pk = profile.getPk()
          const profilePic = await getRemoteFile(`People/${pk}/FaceShots/center`)
          this.props.updateState({
            docValidityViewMode: viewMode,
            docValidityFilterState: currentFilterState,
            profilePic,
            profile,
            comingFromDocValidity: true,
            activeDocumentPortalSection: DocumentCategories.OTHER,
            dataCaptureMode: DataCaptureMode.STANDARD,
            processMode:
              selectedEmploymentStatus === "EMPLOYEE" ? ProcessModes.GENERAL_EMPLOYEE : ProcessModes.GENERAL_CANDIDATE,
          })
          setTimeout(async () => {
            this.closeModals()
            this.props.history.push(`/people/documents`)
          }, 500)
        },
      )
    } catch (error) {
      this.displayWarning(error)
    }
  }

  displayWarning(error: any) {
    let warning = ""
    try {
      if (error.code === "NetworkError") {
        warning = "Seems like your internet connection is down. Reconnect to the network, then try again."
      } else if (error.code === "LocalRequestInstanceCreationError") {
        warning =
          "There was a problem initiating this request. Close PeopleFlow, then login again. If there is still a problem then contact tech support for assistance."
      } else if (
        error.code === "NoClientSettings" ||
        error.code === "NoClientSettingsForSelectedClient" ||
        error.code === "NoClientSettingsInitialised"
      ) {
        warning = "You will need to login again to continue"
      } else if ("message" in error) {
        warning =
          "The following error message was returned:\n\n'" +
          error.message +
          "'. \n\nRefresh the page and try again. If unsuccessful, then contact tech support"
      } else {
        warning =
          "We encountered a problem. Refresh the page and try again. If unsuccessful logout and login, then contact tech support if still unsuccessful"
      }
    } catch (error) {
      warning =
        "We encountered a problem. Refresh the page and try again. If unsuccessful logout and login, then contact tech support if still unsuccessful"
    }

    this.setState({
      ...this.initialModalState,
      warningModalOpen: true,
      warningModalHeader: "Warning",
      warningModalMessage: warning,
    })
  }

  getTableToDisplay(
    uiRefreshTimestamp: number,
    searchString: string,
    tableDataAllTable: Record<string, string>[],
    tableDataPerDocTable: Record<string, string>[],
    tableDataPerPersonTable: Record<string, string>[],
    columnConfigPerPersonTable: ColumnConfig[],
    tableHeight: number,
    initialFilterState: any | undefined,
  ) {
    let perDocumentTable = null
    let perPersonTable = null
    let allTable = null

    if (this.state.viewMode === ViewModes.PER_DOCUMENT) {
      const tableRowData = tableService.applySearch(searchString, tableDataPerDocTable)
      const perDocumentColumnDef = tableService.convertPfColumnConfigToGridColDef(perDocConfig)
      perDocumentTable = (
        <Table
          rows={tableRowData}
          columns={perDocumentColumnDef}
          tableHeight={tableHeight}
          rowHeight={TABLE_ROW_HEIGHT}
          rowSelection={false}
          initialFilterState={initialFilterState}
          onFilterChange={(currentFilterState: any) => this.setState({ currentFilterState })}
          onRowClick={(rowData: Record<string, any>) => this.setFilter(rowData.id, "docType")}
          key={`perDocument_${uiRefreshTimestamp}`}
        />
      )
    } else if (this.state.viewMode === ViewModes.PER_PERSON) {
      const tableRowData = tableService.applySearch(searchString, tableDataPerPersonTable)
      const perPersonColumnDef = tableService.convertPfColumnConfigToGridColDef(columnConfigPerPersonTable)
      perPersonTable = (
        <Table
          rows={tableRowData}
          columns={perPersonColumnDef}
          tableHeight={tableHeight}
          rowHeight={TABLE_ROW_HEIGHT}
          rowSelection={false}
          initialFilterState={initialFilterState}
          onFilterChange={(currentFilterState: any) => this.setState({ currentFilterState })}
          onRowClick={(rowData: Record<string, any>) => this.selectProfile(rowData.id)}
          key={`perPerson_${uiRefreshTimestamp}`}
        />
      )
    }

    const tableRowData = tableService.applySearch(searchString, tableDataAllTable)
    const allColumnDef = tableService.convertPfColumnConfigToGridColDef(columnConfig)
    allTable = (
      <div style={{ visibility: perDocumentTable || perPersonTable ? "hidden" : "visible" }}>
        <Table
          rows={tableRowData}
          columns={allColumnDef}
          tableHeight={tableHeight}
          rowHeight={TABLE_ROW_HEIGHT}
          rowSelection={true}
          onRowClick={(rowData: Record<string, any>) => this.selectProfile(rowData.id)}
          onRowSelection={(selectedRowIds: string[]) => this.rowSelectionHandler(selectedRowIds)}
          initialFilterState={initialFilterState}
          onFilterChange={(currentFilterState: any) => this.setState({ currentFilterState })}
          key={`allDocs_${uiRefreshTimestamp}`}
        />
      </div>
    )
    return (
      <div ref={tableRef} style={{ width: "100%" }}>
        {perDocumentTable}
        {perPersonTable}
        {allTable}
      </div>
    )
  }

  buildLastUpdateMessage() {
    const { cacheTimestamp, checkTimestamp } = this.state
    const cacheAgeInMillisecondes = checkTimestamp - cacheTimestamp

    const millisecondsPerMinute = 1000 * 60
    const millisecondsPerHour = millisecondsPerMinute * 60
    const millisecondsPerDay = millisecondsPerHour * 24
    const daysSinceLastCache = Math.floor(cacheAgeInMillisecondes / millisecondsPerDay)

    if (daysSinceLastCache >= 1) {
      return `Last update: ${daysSinceLastCache} day${daysSinceLastCache > 1 ? "s" : ""} ago`
    }
    const hoursSinceLastCache = Math.floor(cacheAgeInMillisecondes / millisecondsPerHour)
    if (hoursSinceLastCache >= 1) {
      return `Last update: ${hoursSinceLastCache} hour${hoursSinceLastCache > 1 ? "s" : ""} ago`
    }
    const minutesSinceLastCache = Math.floor(cacheAgeInMillisecondes / millisecondsPerMinute)
    if (minutesSinceLastCache >= 1) {
      return `Last update: ${minutesSinceLastCache} minute${minutesSinceLastCache > 1 ? "s" : ""} ago`
    }
    return
  }

  render() {
    const { navMenuAccess, asyncTaskQueueDocExport = [], itemsBeingGenerated = [] } = this.props
    const { hasDocValidityAccess } = navMenuAccess
    const {
      cacheTimestamp,
      downloadQueueModalOpen,
      viewMode,
      isUpdatingDocExportQueue,
      loadingModalOpen,
      loadingModalMessage,
      loadingModalAuxMessage,
      searchString,
      warningModalOpen,
      warningModalHeader,
      warningModalMessage,
      uiRefreshTimestamp,
      tableDataAllTable,
      tableDataPerDocTable,
      tableDataPerPersonTable,
      columnConfigPerPersonTable,
      tableHeight,
      initialFilterState,
      itemsDownloading = [],
    } = this.state

    const toolbarActionButtons: ActionButtonType[] = [
      {
        iconPath: mdiRefresh,
        iconColor: ColorPalette.PRIMARY_BLUE,
        onClick: () => this.refreshAllData(),
        label: "REFRESH",
        title: "Refetch data",
        disabled: false,
      },
    ]

    if (viewMode === ViewModes.ALL) {
      toolbarActionButtons.unshift(
        {
          iconPath: mdiPlaylistPlus,
          iconColor: ColorPalette.PRIMARY_BLUE,
          onClick: () => this.updateQueueData(),
          label: "VIEW QUEUE",
          disabled: false,
        },
        {
          iconPath: mdiFileDocumentMultiple,
          iconColor: ColorPalette.PRIMARY_BLUE,
          onClick: () => this.generateDocs(),
          label: "EXPORT DOCS",
          disabled: false,
        },
      )
    }

    if (cacheTimestamp !== 0) {
      const label = this.buildLastUpdateMessage()
      if (label) {
        toolbarActionButtons.unshift({
          iconPath: mdiMessageAlert,
          iconColor: ColorPalette.SECONDARY_TEXT,
          onClick: () => undefined,
          disabled: true,
          label: label,
        })
      }
    }

    const table = this.getTableToDisplay(
      uiRefreshTimestamp,
      searchString,
      tableDataAllTable,
      tableDataPerDocTable,
      tableDataPerPersonTable,
      columnConfigPerPersonTable,
      tableHeight,
      initialFilterState,
    )

    let downloadQueueModal = null
    if (downloadQueueModalOpen) {
      downloadQueueModal = (
        <DownloadQueueModal
          open={downloadQueueModalOpen}
          downloadQueue={DocsService.asyncTasksToDownloadQueueItems(asyncTaskQueueDocExport)}
          itemsDownloading={itemsDownloading}
          itemsBeingGenerated={itemsBeingGenerated}
          header={"Download Queue"}
          body={""}
          isUpdatingDocExportQueue={isUpdatingDocExportQueue}
          updateQueueData={() => this.updateQueueData()}
          download={(filePath: string, queueId: string) => this.downloadFile(filePath, queueId)}
          dismiss={() => this.closeModals()}
        />
      )
    }
    return (
      <div style={styles.container}>
        <NavBar
          history={this.props.history}
          match={this.props.match}
          location={this.props.location}
          reloadPageData={() => this.reload()}
        />
        <SectionHeader
          ref={sectionHeaderRef}
          style={styles.sectionHeader}
          searchString={searchString}
          textHandler={(e) => this.searchHandler(e)}
          onClick={() => this.setState({ tableWidth: 10 })}>
          {this.props.currentSection}
        </SectionHeader>

        <div style={styles.contentContainer}>
          <SideMenu
            visible={this.state.sideMenuVisible}
            menuComponents={
              <DocValiditySideMenu
                setFilter={(rowItemId: string) => this.setFilter(rowItemId, "status")}
                chartConfig={this.state.chartConfig}
                numberOfDocsTotal={this.state.tableDataAllTable.length}
                selectedViewMode={this.state.viewMode}
                onClick={(viewMode) => this.changeViewMode(viewMode)}
              />
            }
          />
          <div style={styles.rightSide}>
            <Toolbar actionButtons={toolbarActionButtons} key={`actionToolbar_${toolbarActionButtons.length}`} />
            <div style={styles.rightSideContent}>{table}</div>
          </div>
        </div>
        {downloadQueueModal}
        <AlertModalOneButton
          open={warningModalOpen}
          header={warningModalHeader}
          body={warningModalMessage}
          buttonLabel={"Ok"}
          onClick={() => this.closeModals()}
        />
        <AlertModalOneButton
          open={!hasDocValidityAccess}
          header={"Not Authorised"}
          body={"You don't have permission to view time-sensitive documents / competencies."}
          buttonLabel={"Ok"}
          opaqueBackground={true}
          onClick={() => this.props.history.goBack()}
        />
        <LoadingModal open={loadingModalOpen} auxilliaryMessage={loadingModalAuxMessage}>
          {loadingModalMessage}
        </LoadingModal>
      </div>
    )
  }
}

const styles = {
  container: {
    display: "flex",
    flexDirection: "column" as "column",
    flex: 1,
    backgroundImage: `linear-gradient(to bottom, ${ColorPalette.SCREEN_TOP_GRADIENT}, ${ColorPalette.SCREEN_BOTTOM_GRADIENT})`,
    height: "100vh",
  },
  sectionHeader: {
    margin: "3.5% auto 1.5%",
  },
  contentContainer: {
    display: "flex",
    flex: 1,
    overflow: "auto",
  },
  rightSide: {
    display: "flex",
    flexDirection: "column" as "column",
    paddingInline: "max(2em, 2%)",
    overflow: "hidden",
    width: "100%",
  },
  rightSideContent: {
    boxShadow: "0px -1px 8px rgba(60,60,60, 0.1)",
    display: "flex",
    backgroundColor: ColorPalette.CARD_WHITE,
    flex: 1,
  },
  tableName: {
    marginTop: window.innerHeight * 0.055,
    height: window.innerHeight * 0.7,
    width: "94%",
  },
  newFormButton: {
    marginTop: window.innerHeight * 0.045,
    fontWeight: "bolder",
    fontSize: "0.8rem",
    color: ColorPalette.SECONDARY_TEXT,
    height: 40,
    ":hover": {
      color: ColorPalette.PRIMARY_BLUE,
    },
    ":active": {
      color: ColorPalette.DARK_GREY,
    },
  },
  tableNavButtonContainer: {
    marginBottom: window.innerHeight * 0.005,
    display: "flex",
    alignItems: "center",
    width: (window.innerWidth * 10) / 12,
  },
  toolbarButton: {
    marginTop: window.innerHeight * 0,
    width: 220,
    fontWeight: "bolder" as "bolder",
    fontSize: "0.8rem",
    color: ColorPalette.SECONDARY_TEXT,
    height: 40,
    ":hover": {
      color: ColorPalette.PRIMARY_BLUE,
    },
    ":active": {
      color: ColorPalette.DARK_GREY,
    },
  },
  statusDotContainer: {
    width: "100%",
    height: "100%",
    display: "flex",
    justifyContent: "flex-start" as "flex-start",
    alignItems: "center" as "center",
    paddingLeft: 10,
  },
  statusDot: {
    width: 10,
    height: 10,
    borderRadius: 30,
  },
}

const mapStateToProps = (state: PeopleFlowCombinedReducer) => {
  return {
    idPassport: state.sessionManager.idPassport,
    password: state.sessionManager.password,
    associationRepo: state.sessionManager.associationRepo as AssociationSettingsRepository,
    profileRepo: state.sessionManager.profileRepo as ProfileRepository,
    userRepo: state.sessionManager.userRepo as UsersRepository,
    activeDocumentPortalSection: state.sessionManager.activeDocumentPortalSection,
    selectedAssociation: state.sessionManager.selectedAssociation,
    selectedCohort: state.sessionManager.selectedCohort,
    selectedRole: state.sessionManager.selectedRole,
    selectedEmploymentStatus: state.sessionManager.selectedEmploymentStatus,
    currentSection: state.sessionManager.currentSection,
    currentScreen: state.sessionManager.currentScreen,
    previousScreen: state.sessionManager.previousScreen,
    navMenuAccess: state.sessionManager.navMenuAccess,
    processMode: state.sessionManager.processMode,
    profile: state.sessionManager.profile,
    profilePic: state.sessionManager.profilePic,
    dataCaptureMode: state.sessionManager.dataCaptureMode,
    comingFromDocValidity: state.sessionManager.comingFromDocValidity,
    docValidityViewMode: state.sessionManager.docValidityViewMode,
    docValidityFilterState: state.sessionManager.docValidityFilterState,
    asyncTaskQueueDocExport: state.sessionManager.asyncTaskQueueDocExport,
    associationsWithInitialProfileLoad: state.sessionManager.associationsWithInitialProfileLoad,
    itemsBeingGenerated: state.sessionManager.itemsBeingGenerated,
  }
}

const mapDispatchToProps = (dispatch: any) => {
  return {
    updateState: (data: any) => dispatch({ type: ActionType.UPDATE_STATE, data }),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Radium(DocValidity))
